import i18n from "i18next";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", country: "US" },
  { code: "sq", label: "Albanian", country: "AL" },
  { code: "am", label: "Amharic", country: "ET" },
  { code: "ar", label: "Arabic", country: "SA" },
  { code: "az", label: "Azerbaijani", country: "AZ" },
  { code: "eu", label: "Basque", country: "ES" },
  { code: "bn", label: "Bengali", country: "IN" },
  { code: "bs", label: "Bosnian", country: "BA" },
  { code: "bg", label: "Bulgarian", country: "BG" },
  { code: "my", label: "Burmese", country: "MM" },
  { code: "ca", label: "Catalan", country: "ES" },
  { code: "zh", label: "Chinese", country: "CN" },
  { code: "hr", label: "Croatian", country: "HR" },
  { code: "cs", label: "Czech", country: "CZ" },
  { code: "da", label: "Danish", country: "DK" },
  { code: "nl", label: "Dutch", country: "NL" },
  { code: "et", label: "Estonian", country: "EE" },
  { code: "fi", label: "Finnish", country: "FI" },
  { code: "fr", label: "French", country: "FR" },
  { code: "gl", label: "Galician", country: "ES" },
  { code: "ka", label: "Georgian", country: "GE" },
  { code: "de", label: "German", country: "DE" },
  { code: "el", label: "Greek", country: "GR" },
  { code: "gu", label: "Gujarati", country: "IN" },
  { code: "he", label: "Hebrew", country: "IL" },
  { code: "hi", label: "Hindi", country: "IN" },
  { code: "hu", label: "Hungarian", country: "HU" },
  { code: "id", label: "Indonesian", country: "ID" },
  { code: "it", label: "Italian", country: "IT" },
  { code: "ja", label: "Japanese", country: "JP" },
  { code: "km", label: "Khmer", country: "KH" },
  { code: "ko", label: "Korean", country: "KR" },
  { code: "lo", label: "Lao", country: "LA" },
  { code: "lv", label: "Latvian", country: "LV" },
  { code: "lt", label: "Lithuanian", country: "LT" },
  { code: "mk", label: "Macedonian", country: "MK" },
  { code: "ms", label: "Malay", country: "MY" },
  { code: "ml", label: "Malayalam", country: "IN" },
  { code: "mn", label: "Mongolian", country: "MN" },
  { code: "nb", label: "Norwegian", country: "NO" },
  { code: "fa", label: "Persian", country: "IR" },
  { code: "pl", label: "Polish", country: "PL" },
  { code: "pt", label: "Portuguese", country: "PT" },
  { code: "ro", label: "Romanian", country: "RO" },
  { code: "ru", label: "Russian", country: "RU" },
  { code: "sr", label: "Serbian", country: "RS" },
  { code: "sk", label: "Slovak", country: "SK" },
  { code: "sl", label: "Slovenian", country: "SI" },
  { code: "es", label: "Spanish", country: "ES" },
  { code: "sv", label: "Swedish", country: "SE" },
  { code: "tl", label: "Tagalog", country: "PH" },
  { code: "te", label: "Telugu", country: "IN" },
  { code: "th", label: "Thai", country: "TH" },
  { code: "tr", label: "Turkish", country: "TR" },
  { code: "uk", label: "Ukrainian", country: "UA" },
  { code: "vi", label: "Vietnamese", country: "VN" },
];

/**
 * Resolves a language code from string, object, or ID.
 * @param {string|object|number} input - Preferred language identifier or object
 * @param {Array} [languagesList] - Optional list of languages from API (e.g. LanguageList())
 * @returns {string|null} - Language code (e.g., 'en', 'ar', 'ml') or null
 */
export function resolveLanguageCode(input, languagesList = []) {
  if (!input) return null;

  // 1. Direct string code or name
  if (typeof input === "string") {
    const trimmed = input.trim().toLowerCase();
    const matchByCode = SUPPORTED_LANGUAGES.find(
      (l) => l.code.toLowerCase() === trimmed
    );
    if (matchByCode) return matchByCode.code;

    const matchByLabel = SUPPORTED_LANGUAGES.find(
      (l) => l.label.toLowerCase() === trimmed
    );
    if (matchByLabel) return matchByLabel.code;
  }

  // 2. Object with iso_code, code, name, or label
  if (typeof input === "object" && input !== null) {
    const rawCode = input.iso_code || input.code;
    if (rawCode && typeof rawCode === "string") {
      const trimmedCode = rawCode.trim().toLowerCase();
      const matchByCode = SUPPORTED_LANGUAGES.find(
        (l) => l.code.toLowerCase() === trimmedCode
      );
      if (matchByCode) return matchByCode.code;
    }

    const rawName = input.name || input.label;
    if (rawName && typeof rawName === "string") {
      const trimmedName = rawName.trim().toLowerCase();
      const matchByLabel = SUPPORTED_LANGUAGES.find(
        (l) => l.label.toLowerCase() === trimmedName
      );
      if (matchByLabel) return matchByLabel.code;
    }

    // Lookup by ID in provided languages list if available
    if (input.id && Array.isArray(languagesList) && languagesList.length > 0) {
      const matchInList = languagesList.find(
        (l) => String(l.id || l.value) === String(input.id)
      );
      if (matchInList) {
        return resolveLanguageCode(matchInList);
      }
    }
  }

  // 3. Numeric ID lookup in languagesList
  if (typeof input === "number" || (!isNaN(input) && typeof input === "string")) {
    if (Array.isArray(languagesList) && languagesList.length > 0) {
      const matchInList = languagesList.find(
        (l) => String(l.id || l.value) === String(input)
      );
      if (matchInList) {
        return resolveLanguageCode(matchInList);
      }
    }
  }

  return null;
}

export function applySystemLanguage(languageInput, languagesList = []) {
  try {
    const code = resolveLanguageCode(languageInput, languagesList);
    if (!code) return;

    const currentBase = (i18n.language || "").split("-")[0].toLowerCase();
    const targetBase = code.toLowerCase();

    localStorage.setItem("i18nextLng", targetBase);

    if (currentBase !== targetBase) {
      i18n.changeLanguage(targetBase);
    }
  } catch (err) {
    console.error("Error applying system language:", err);
  }
}

